'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { es } from 'date-fns/locale'
import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  ArrowLeftRight,
  Calendar as CalendarIcon,
  Download,
  FileJson,
  Loader2,
  Percent,
  RefreshCw,
  Save,
  Settings,
  Trash2,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { DateRange } from 'react-day-picker'

import { Header } from '@/components/layout/Header'
import { FunctionsService } from '@/services/functions.service'
import { useAuth } from '@/hooks/useAuth';
import { useCompany } from '@/hooks/useCompany'
import { useSalesReport } from '@/hooks/useSalesReport'
import { useToast } from '@/hooks/useToast'
import { cn, formatCurrency, formatDateTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

// -------------------- Types --------------------

type SellerRole = 'principal' | 'secundario' | 'ninguno'

interface SummaryMetrics {
  total: number
  orders: number
  avg: number
  totalUnits: number
  upt: number
}

interface SellerGoal {
  name: string
  role: SellerRole
  goalPercentage: number
  goalAmount?: number // New: monetary value (e.g., 15000 instead of 36.96%)
}

interface DailyWeight {
  principal: number
  secundario: number
}

interface MonthlyGoal {
  total: number
  previousYearTotal?: number
  sellerGoals: SellerGoal[]
  dailyWeights: Record<number, DailyWeight>
  dayImportance?: Record<number, number> // 0-6 (Sunday-Saturday), value 1-5 for importance
}

interface BudgetConfig {
  // Legacy single month support
  total: number
  sellerGoals: SellerGoal[]
  dailyWeights: Record<number, DailyWeight>

  // New multi-month support (January = 0, December = 11)
  monthlyGoals?: Record<number, MonthlyGoal>

  // Current active month (for UI display)
  currentMonth?: number
}

// Shape used by the Daily table (kept flexible to match backend if available)
interface SellerWithGoalMeta {
  name: string
  role: SellerRole
  goalPercentage: number
}

interface DailyRowSellerData {
  actual: number
  goal: number
}

interface DailyTableDataShape {
  sellersWithGoals: SellerWithGoalMeta[]
  sellersWithoutGoals: string[]
  days: Array<{
    date: string
    dayName: string
    estimatedSales: number
    totalDaySales: number
    fulfillment: number
    sellerData: Record<string, DailyRowSellerData>
    otherSellersData: Record<string, number>
  }>
}

// -------------------- Constants --------------------

const DEFAULT_RANGE: DateRange = {
  from: startOfDay(subDays(new Date(), 6)),
  to: endOfDay(new Date()),
}

const allSellersForBudget: { name: string }[] = [
  { name: 'Daniela P.' },
  { name: 'Sebastian R.' },
  { name: 'Allanise N.' },
  { name: 'Jose P.' },
  { name: 'Juliana A.' },
  { name: 'Tayron M.' },
  { name: 'William Sierra (1)' },
  { name: 'William Sierra (2)' },
  { name: 'Yameilyz P.' },
  { name: 'Brian J.' },
  { name: 'Ivan L.' },
  { name: 'Lyangelis M.' },
  { name: 'Scarlett D.' },
  { name: 'Roberto C.' },
  { name: 'Gellymar C.' },
  { name: 'Online' },
]

interface LoadedPdfLogo {
  dataUrl: string
  format: 'PNG' | 'JPEG' | 'WEBP'
  width: number
  height: number
}

/**
 * Loads a logo (same-origin path or remote URL) as a base64 data URL plus its
 * natural dimensions, so jsPDF can embed it without tainting a canvas.
 * Returns null on any failure (missing file, CORS, decode error).
 */
async function loadPdfLogo(url: string): Promise<LoadedPdfLogo | null> {
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const blob = await res.blob()
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
    const format: LoadedPdfLogo['format'] = blob.type.includes('jpeg') || blob.type.includes('jpg')
      ? 'JPEG'
      : blob.type.includes('webp')
      ? 'WEBP'
      : 'PNG'
    const { width, height } = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 })
      img.onerror = () => reject(new Error('decode error'))
      img.src = dataUrl
    })
    return { dataUrl, format, width, height }
  } catch {
    return null
  }
}

/**
 * Tries each candidate (data URL, Storage URL, local path) in order and returns
 * the first that loads. Storage URLs can fail due to CORS on canvas access;
 * embedded base64 (pdfLogoData) and same-origin assets always work.
 */
async function loadFirstPdfLogo(
  candidates: (string | null | undefined)[]
): Promise<LoadedPdfLogo | null> {
  for (const url of candidates) {
    if (!url) continue
    const logo = await loadPdfLogo(url)
    if (logo) return logo
  }
  return null
}

/**
 * Draws the logo right-aligned inside a bounding box while preserving aspect
 * ratio. `rightEdge`/`top` are in mm; box defaults to 30x18mm.
 */
function addLogoToPdf(
  doc: jsPDF,
  logo: LoadedPdfLogo,
  rightEdge: number,
  top: number,
  maxW = 30,
  maxH = 18
) {
  const ratio = Math.min(maxW / logo.width, maxH / logo.height)
  const w = logo.width * ratio
  const h = logo.height * ratio
  doc.addImage(logo.dataUrl, logo.format, rightEdge - w, top, w, h)
}

const daysOfWeek = [
  { index: 0, label: 'Domingo' },
  { index: 1, label: 'Lunes' },
  { index: 2, label: 'Martes' },
  { index: 3, label: 'Miércoles' },
  { index: 4, label: 'Jueves' },
  { index: 5, label: 'Viernes' },
  { index: 6, label: 'Sábado' },
]

const defaultDailyWeights: Record<number, DailyWeight> = {
  0: { principal: 1.204166666666667, secundario: 2.178888888888889 },  // Domingo
  1: { principal: 0.641666666666667, secundario: 1.160555555555556 },  // Lunes
  2: { principal: 0.7225, secundario: 1.307222222222222 },             // Martes
  3: { principal: 0.963333333333333, secundario: 1.742777777777778 },  // Miércoles
  4: { principal: 1.2125, secundario: 2.193333333333333 },             // Jueves
  5: { principal: 1.844722222222222, secundario: 2.648333333333333 },  // Viernes
  6: { principal: 1.844722222222222, secundario: 3.337777777777778 },  // Sábado
}

const defaultSellerGoals: SellerGoal[] = allSellersForBudget.map(({ name }) => {
  switch (name) {
    case 'Sebastian R.':
      return { name, role: 'principal', goalPercentage: 36.956521739130434 }
    case 'Daniela P.':
    case 'Tayron M.':
    case 'Brian J.':
    case 'Ivan L.':
    case 'Lyangelis M.':
    case 'Scarlett D.':
      return { name, role: 'secundario', goalPercentage: 15.760869565217391 }
    default:
      return { name, role: 'ninguno', goalPercentage: 0 }
  }
})

const defaultBudget: BudgetConfig = {
  total: 46000,
  sellerGoals: defaultSellerGoals,
  dailyWeights: defaultDailyWeights,
}

const CACHE_SOURCE_LABELS: Record<string, string> = {
  memory: 'Cache (Memoria)',
  hot: 'Cache (Hot)',
  cold: 'Cache (Cold)',
  api: 'Shopify API',
}

const formatSellerName = (fullName: string | undefined) => {
  if (!fullName) return 'Sin asignar'
  if (fullName === 'Venta Online') return fullName
  const parts = fullName.split(' ')
  if (parts.length > 1) {
    return `${parts[0]} ${parts[1].charAt(0)}.`
  }
  return fullName
}

// -------------------- Intelligent Daily Weight Calculation --------------------

/**
 * Calculates daily weights based on day importance levels
 * @param dayImportance - Record of day index (0-6) to importance level (1-5)
 * @param month - The month (0-11) to calculate weights for
 * @param year - The year to calculate weights for
 * @returns Daily weights for principal and secundario roles
 */
function calculateIntelligentDailyWeights(
  dayImportance: Record<number, number>,
  month: number,
  year: number
): Record<number, DailyWeight> {
  // Count occurrences of each day in the month
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const dayCounts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day)
    const dayOfWeek = date.getDay()
    dayCounts[dayOfWeek]++
  }

  // Calculate total importance score for the month
  let totalImportance = 0
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const importance = dayImportance[dayIndex] || 3 // Default to medium importance
    totalImportance += importance * dayCounts[dayIndex]
  }

  // Calculate weights as percentage of month
  const weights: Record<number, DailyWeight> = {}
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const importance = dayImportance[dayIndex] || 3
    const percentage = ((importance * dayCounts[dayIndex]) / totalImportance) * 100

    // Principal sellers get base percentage
    // Secundario sellers get 1.8x multiplier (higher contribution expected)
    weights[dayIndex] = {
      principal: percentage,
      secundario: percentage * 1.8,
    }
  }

  return weights
}

/**
 * Gets the budget configuration for a specific month, with fallback to current/default
 */
function getBudgetForMonth(budget: BudgetConfig, month: number): {
  total: number
  previousYearTotal?: number
  sellerGoals: SellerGoal[]
  dailyWeights: Record<number, DailyWeight>
  dayImportance?: Record<number, number>
} {
  // If monthlyGoals exists and has data for this month, use it
  if (budget.monthlyGoals && budget.monthlyGoals[month]) {
    return budget.monthlyGoals[month]
  }

  // Otherwise, fallback to legacy single-month budget
  return {
    total: budget.total,
    previousYearTotal: undefined,
    sellerGoals: budget.sellerGoals,
    dailyWeights: budget.dailyWeights,
    dayImportance: undefined,
  }
}

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

const MONTHLY_GOALS_STORAGE_KEY = 'nibpr-monthly-goals'

function loadMonthlyGoalsFromStorage(): Record<number, MonthlyGoal> | undefined {
  try {
    const raw = localStorage.getItem(MONTHLY_GOALS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : undefined
  } catch {
    return undefined
  }
}

function saveMonthlyGoalsToStorage(monthlyGoals: Record<number, MonthlyGoal> | undefined) {
  try {
    if (monthlyGoals) {
      localStorage.setItem(MONTHLY_GOALS_STORAGE_KEY, JSON.stringify(monthlyGoals))
    }
  } catch {
    // localStorage not available
  }
}

// -------------------- Component --------------------

export function StatsDashboard() {
  const { user } = useAuth()
  const company = useCompany()
  const { toast } = useToast()

  // Real sellers (from /vendedores) used to build the monthly budget config.
  // Falls back to the built-in list until they load or if the fetch fails.
  const [budgetSellers, setBudgetSellers] = useState<{ name: string }[]>(allSellersForBudget)

  useEffect(() => {
    let cancelled = false
    FunctionsService.getSellers().then((res) => {
      if (cancelled) return
      const list = res.data?.sellers
      if (!list || list.length === 0) return
      const seen = new Set<string>()
      const names = list
        .filter((s) => s.active !== false)
        .map((s) => (s.shortName && s.shortName.trim()) || s.name)
        .filter((name) => {
          if (!name || seen.has(name)) return false
          seen.add(name)
          return true
        })
        .map((name) => ({ name }))
      if (names.length > 0) setBudgetSellers(names)
    })
    return () => {
      cancelled = true
    }
  }, [])
  const { report, loading, error, getSalesReport, clearReport } = useSalesReport()
  const { report: compareReport, loading: compareLoading, getSalesReport: getCompareReport } = useSalesReport()

  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE)
  const [compareMode, setCompareMode] = useState(false)
  const [compareDateRange, setCompareDateRange] = useState<DateRange>({ from: undefined, to: undefined })
  const [budget, setBudget] = useState<BudgetConfig>(() => {
    const storedMonthlyGoals = loadMonthlyGoalsFromStorage()
    if (storedMonthlyGoals) {
      return { ...defaultBudget, monthlyGoals: storedMonthlyGoals }
    }
    return defaultBudget
  })
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth()) // 0-11
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [showOnlyGoalSellers, setShowOnlyGoalSellers] = useState(false)
  const [isBudgetDialogOpen, setIsBudgetDialogOpen] = useState(false)
  const [hasBudgetBeenEdited, setHasBudgetBeenEdited] = useState(false)
  const [forceShopify, setForceShopify] = useState(false)

  // Get budget for the selected month
  const currentMonthBudget = useMemo(() => {
    return getBudgetForMonth(budget, selectedMonth)
  }, [budget, selectedMonth])

  // ---- Normalize budget from server snapshot (if present) ----
  useEffect(() => {
    const snapshot = (report as any)?.budgetSnapshot
    if (!snapshot || hasBudgetBeenEdited) return

    setBudget((prev) => {
      const normalizedBudget: BudgetConfig = {
        total: snapshot.total ?? prev.total ?? defaultBudget.total,
        sellerGoals: budgetSellers.map(({ name }) => {
          const incoming =
            snapshot.sellerGoals?.find((goal: any) => goal.name === name) ??
            prev.sellerGoals.find((goal) => goal.name === name)

          return {
            name,
            role: (incoming?.role as SellerRole) ?? 'ninguno',
            goalPercentage: Number(incoming?.goalPercentage) || 0,
          }
        }),
        dailyWeights: { ...defaultDailyWeights, ...(snapshot.dailyWeights ?? prev.dailyWeights) },
        monthlyGoals: snapshot.monthlyGoals ?? prev.monthlyGoals ?? loadMonthlyGoalsFromStorage(),
        currentMonth: snapshot.currentMonth ?? prev.currentMonth,
      }

      const prevSerialized = JSON.stringify(prev)
      const nextSerialized = JSON.stringify(normalizedBudget)
      if (prevSerialized === nextSerialized) {
        return prev
      }

      return normalizedBudget
    })
    setHasBudgetBeenEdited(false)
  }, [report, hasBudgetBeenEdited, budgetSellers])

  // ---- Safe daily data (keeps UI functional even if backend shape differs) ----
  const dailyReportData: DailyTableDataShape | [] = useMemo(() => {
    const raw: any = (report as any)?.dailyReportData
    if (
      raw &&
      raw.days &&
      Array.isArray(raw.days) &&
      Array.isArray(raw.sellersWithGoals) &&
      Array.isArray(raw.sellersWithoutGoals)
    ) {
      return raw as DailyTableDataShape
    }

    if (!report || !dateRange.from || !dateRange.to) {
      return []
    }

    const normalizeName = (name: string) => name.trim().toLowerCase()

    const startDate = startOfDay(dateRange.from)
    const endDate = startOfDay(dateRange.to)

    const goalMap = new Map<string, SellerGoal>()
    currentMonthBudget.sellerGoals.forEach((goal) => {
      goalMap.set(normalizeName(goal.name), goal)
    })

    const goalSellersConfig = currentMonthBudget.sellerGoals.filter((goal) => goal.role !== 'ninguno')

    type DayAccumulator = {
      goalSales: Map<string, number>
      otherSales: Map<string, number>
      totalSales: number
    }

    const salesByDay = new Map<string, DayAccumulator>()
    const otherSellerSet = new Set<string>()

    const orders = report.orders ?? []
    orders.forEach((order) => {
      const rawDate: any = (order as any).date
      let orderDate: Date | null = null

      if (rawDate instanceof Date) {
        orderDate = rawDate
      } else if (rawDate && typeof rawDate === 'object' && typeof rawDate.toDate === 'function') {
        orderDate = rawDate.toDate()
      } else if (typeof rawDate === 'string' || typeof rawDate === 'number') {
        const parsed = new Date(rawDate)
        orderDate = Number.isNaN(parsed.getTime()) ? null : parsed
      }

      if (!orderDate) return

      const orderDay = startOfDay(orderDate)
      if (
        differenceInCalendarDays(orderDay, startDate) < 0 ||
        differenceInCalendarDays(orderDay, endDate) > 0
      ) {
        return
      }

      const sellerName =
        (order as any).sellerName ?? (order as any).seller_name ?? 'Sin asignar'
      const normalizedSeller = normalizeName(sellerName)
      const goalConfig = goalMap.get(normalizedSeller)
      const displayName = goalConfig?.name ?? sellerName

      let total = Number((order as any).netSales ?? (order as any).net_sales ?? 0)
      if (!Number.isFinite(total)) {
        total = 0
      }

      // Exclude internal sales based on order note
      const orderNote = (order as any).note?.toLowerCase() || '';
      if (orderNote.includes('venta_interna') && total < 0) {
        total = 0;
      }

      if (!Number.isFinite(total)) return

      const dayKey = format(orderDay, 'yyyy-MM-dd')
      if (!salesByDay.has(dayKey)) {
        salesByDay.set(dayKey, {
          goalSales: new Map(),
          otherSales: new Map(),
          totalSales: 0,
        })
      }

      const dayBucket = salesByDay.get(dayKey)!
      if (goalConfig && goalConfig.goalPercentage > 0 && goalConfig.role !== 'ninguno') {
        const current = dayBucket.goalSales.get(goalConfig.name) ?? 0
        dayBucket.goalSales.set(goalConfig.name, current + total)
      } else {
        const current = dayBucket.otherSales.get(displayName) ?? 0
        dayBucket.otherSales.set(displayName, current + total)
        otherSellerSet.add(displayName)
      }

      dayBucket.totalSales += total
    })

    const sellersWithGoals: SellerWithGoalMeta[] = goalSellersConfig.map((goal) => ({
      name: goal.name,
      role: goal.role,
      goalPercentage: goal.goalPercentage,
    }))

    const sellersWithoutGoals = Array.from(otherSellerSet)

    const monthStart = startOfDay(new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1))
    const monthEnd = endOfMonth(monthStart)
    const roleMonthlyWeightTotals = { principal: 0, secundario: 0 }
    for (let cursor = monthStart; cursor <= monthEnd; cursor = addDays(cursor, 1)) {
      const weight = currentMonthBudget.dailyWeights[cursor.getDay()] ?? defaultDailyWeights[cursor.getDay()] ?? {
        principal: 0,
        secundario: 0,
      }
      roleMonthlyWeightTotals.principal += weight.principal ?? 0
      roleMonthlyWeightTotals.secundario += weight.secundario ?? 0
    }

    const days: DailyTableDataShape['days'] = []
    for (let current = startDate; current <= endDate; current = addDays(current, 1)) {
      const dayKey = format(current, 'yyyy-MM-dd')
      const bucket = salesByDay.get(dayKey)
      const dayOfWeek = current.getDay()
      const dayWeight = currentMonthBudget.dailyWeights[dayOfWeek] ?? defaultDailyWeights[dayOfWeek] ?? {
        principal: 0,
        secundario: 0,
      }

      const sellerData: Record<string, DailyRowSellerData> = {}
      const otherSellersData: Record<string, number> = {}

      let estimatedSales = 0
      let totalDaySales = 0

      sellersWithGoals.forEach((goal) => {
        const sellerSales = bucket?.goalSales.get(goal.name) ?? 0
        totalDaySales += sellerSales

        const sellerPercent = goal.goalPercentage ?? 0
        const dayPercent = goal.role === 'principal' ? dayWeight.principal ?? 0 : dayWeight.secundario ?? 0
        const monthlyTotal =
          goal.role === 'principal'
            ? roleMonthlyWeightTotals.principal
            : roleMonthlyWeightTotals.secundario

        const dailyGoal =
          monthlyTotal > 0 && sellerPercent > 0 && dayPercent > 0
            ? currentMonthBudget.total * (dayPercent / monthlyTotal) * (sellerPercent / 100)
            : 0

        estimatedSales += dailyGoal
        sellerData[goal.name] = {
          actual: sellerSales,
          goal: dailyGoal,
        }
      })

      sellersWithoutGoals.forEach((name) => {
        const sales = bucket?.otherSales.get(name) ?? 0
        totalDaySales += sales
        otherSellersData[name] = sales
      })

      const fulfillment =
        estimatedSales > 0 ? (totalDaySales / estimatedSales) * 100 : 0

      days.push({
        date: format(current, 'dd/MM/yyyy'),
        dayName: format(current, 'EEEE', { locale: es }),
        estimatedSales,
        totalDaySales,
        fulfillment,
        sellerData,
        otherSellersData,
      })
    }

    return {
      sellersWithGoals,
      sellersWithoutGoals,
      days,
    }
  }, [report, currentMonthBudget, dateRange])

  const hasDailyData = !Array.isArray(dailyReportData)
  const dailyDays = hasDailyData ? dailyReportData.days : []

  const rawSellersWithGoals = hasDailyData ? dailyReportData.sellersWithGoals : []
  const rawSellersWithoutGoals = hasDailyData ? dailyReportData.sellersWithoutGoals : []

  const goalSellers = useMemo(
    () => rawSellersWithGoals.filter((seller) => (seller.goalPercentage ?? 0) > 0),
    [rawSellersWithGoals]
  )

  // Note: sellerActualTotals was unused, commented out for future reference
  // const sellerActualTotals = useMemo(() => {
  //   const totals = new Map<string, number>()
  //   dailyDays.forEach((day) => {
  //     Object.entries(day.sellerData).forEach(([name, data]) => {
  //       totals.set(name, (totals.get(name) ?? 0) + (data.actual ?? 0))
  //     })
  //   })
  //   return totals
  // }, [dailyDays])

  const displaySellersWithGoals = useMemo(() => {
    if (!showOnlyGoalSellers) return rawSellersWithGoals
    // Cuando "Solo con metas" está activo, mostrar solo vendedores con meta > 0
    return rawSellersWithGoals.filter((seller) =>
      (seller.goalPercentage ?? 0) > 0
    )
  }, [rawSellersWithGoals, showOnlyGoalSellers])

  const otherSellerTotals = useMemo(() => {
    const totals = new Map<string, number>()
    dailyDays.forEach((day) => {
      Object.entries(day.otherSellersData).forEach(([name, amount]) => {
        totals.set(name, (totals.get(name) ?? 0) + amount)
      })
    })
    return totals
  }, [dailyDays])

  const displaySellersWithoutGoals = useMemo(() => {
    if (!showOnlyGoalSellers) return rawSellersWithoutGoals
    return rawSellersWithoutGoals.filter((name) => (otherSellerTotals.get(name) ?? 0) > 0)
  }, [rawSellersWithoutGoals, showOnlyGoalSellers, otherSellerTotals])

  // ---- Derived: period budget (total de Venta Estimada del período) ----
  const periodBudget = useMemo(() => {
    if (dailyDays.length > 0) {
      return dailyDays.reduce((sum, day) => sum + day.estimatedSales, 0)
    }

    const summaryEstimate = (report as any)?.summary?.estimatedTotal
    if (typeof summaryEstimate === 'number') {
      return summaryEstimate
    }

    if (!dateRange.from || !dateRange.to) {
      return currentMonthBudget.total
    }

    const from = startOfDay(dateRange.from)
    const to = startOfDay(dateRange.to)

    const monthStart = startOfDay(new Date(from.getFullYear(), from.getMonth(), 1))
    const monthEnd = endOfMonth(monthStart)

    let monthlyPrincipalTotal = 0
    let monthlySecondaryTotal = 0

    for (let cursor = monthStart; cursor <= monthEnd; cursor = addDays(cursor, 1)) {
      const weights =
        currentMonthBudget.dailyWeights[cursor.getDay()] ?? defaultDailyWeights[cursor.getDay()] ?? {
          principal: 0,
          secundario: 0,
        }
      monthlyPrincipalTotal += weights.principal ?? 0
      monthlySecondaryTotal += weights.secundario ?? 0
    }

    let total = 0
    for (let current = from; current <= to; current = addDays(current, 1)) {
      const weights =
        currentMonthBudget.dailyWeights[current.getDay()] ?? defaultDailyWeights[current.getDay()] ?? {
          principal: 0,
          secundario: 0,
        }

      currentMonthBudget.sellerGoals.forEach((goal) => {
        const sellerPercent = goal.goalPercentage ?? 0
        if (sellerPercent <= 0) return

        const dayPercent =
          goal.role === 'principal' ? weights.principal ?? 0 : weights.secundario ?? 0
        const monthlyTotal =
          goal.role === 'principal' ? monthlyPrincipalTotal : monthlySecondaryTotal

        if (monthlyTotal > 0 && dayPercent > 0) {
          total += currentMonthBudget.total * (dayPercent / monthlyTotal) * (sellerPercent / 100)
        }
      })
    }

    return total
  }, [report, dailyDays, currentMonthBudget, dateRange])

  // ---- Derived: period actual sales (total de Venta Real del período) ----
  const periodActualSales = useMemo(() => {
    if (dailyDays.length > 0) {
      return dailyDays.reduce((sum, day) => sum + day.totalDaySales, 0)
    }
    return report?.totalNetSales ?? 0
  }, [dailyDays, report])

  // ---- Derived: period fulfillment (cumplimiento del período basado en totales) ----
  const periodFulfillment = useMemo(() => {
    if (periodBudget > 0) {
      return (periodActualSales / periodBudget) * 100
    }
    return 0
  }, [periodActualSales, periodBudget])

  // ---- Derived: period taxes (total de impuestos del período) ----
  const periodTaxes = useMemo(() => {
    if (!report?.orders) return 0
    return report.orders.reduce((sum, order) => {
      const tax = Number((order as any).tax ?? 0)
      return sum + tax
    }, 0)
  }, [report])

  // ---- Comparison: daily totals from the compare report ----
  const compareDailyData = useMemo(() => {
    if (!compareReport?.orders || !compareDateRange.from || !compareDateRange.to) return []
    const dayMap = new Map<string, number>()
    compareReport.orders.forEach((order) => {
      const raw: any = (order as any).date
      let d: Date | null = null
      if (raw instanceof Date) d = raw
      else if (raw && typeof raw.toDate === 'function') d = raw.toDate()
      else if (typeof raw === 'string' || typeof raw === 'number') {
        const p = new Date(raw)
        d = isNaN(p.getTime()) ? null : p
      }
      if (!d) return
      const key = format(startOfDay(d), 'yyyy-MM-dd')
      dayMap.set(key, (dayMap.get(key) || 0) + (Number((order as any).netSales) || 0))
    })
    const result: { date: string; totalDaySales: number }[] = []
    for (
      let cur = startOfDay(compareDateRange.from);
      !isBefore(startOfDay(compareDateRange.to), cur);
      cur = addDays(cur, 1)
    ) {
      result.push({ date: format(cur, 'dd/MM'), totalDaySales: dayMap.get(format(cur, 'yyyy-MM-dd')) || 0 })
    }
    return result
  }, [compareReport, compareDateRange])

  // ---- Comparison: summary metrics ----
  const compareMetrics = useMemo(() => {
    if (!compareReport) return null
    const total = compareReport.totalNetSales ?? 0
    const orders = compareReport.totalOrders ?? 0
    const avg = orders > 0 ? total / orders : 0
    let units = 0
    compareReport.orders?.forEach((o) => {
      const products = (o as any).products || []
      products.forEach((p: any) => { units += Number(p.quantity || 0) })
    })
    const upt = orders > 0 ? units / orders : 0
    return { total, orders, avg, units, upt }
  }, [compareReport])

  // ---- Comparison: chart data (align periods by day index) ----
  const compareChartData = useMemo(() => {
    const len = Math.max(dailyDays.length, compareDailyData.length)
    return Array.from({ length: len }, (_, i) => ({
      dia: i + 1,
      actual: dailyDays[i]?.totalDaySales ?? 0,
      anterior: compareDailyData[i]?.totalDaySales ?? 0,
      labelActual: dailyDays[i]?.date ?? `Día ${i + 1}`,
      labelAnterior: compareDailyData[i]?.date ?? `Día ${i + 1}`,
    }))
  }, [dailyDays, compareDailyData])

  // ---- Summary metrics (minimal but useful) ----
  const summaryMetrics = useMemo<SummaryMetrics>(() => {
    const total = report?.totalNetSales ?? 0
    const orders = report?.totalOrders ?? 0
    const avg = orders > 0 ? total / orders : 0

    // Calculate total units
    let totalUnits = 0
    if (report?.orders) {
      report.orders.forEach((order) => {
        const products = (order as any).products || []
        products.forEach((product: any) => {
          totalUnits += Number(product.quantity || 0)
        })
      })
    }

    // Calculate UPT (Units Per Transaction)
    const upt = orders > 0 ? totalUnits / orders : 0

    return { total, orders, avg, totalUnits, upt }
  }, [report])

  // ---- Comparison: deltas (needs summaryMetrics, so declared here) ----
  const compareDeltas = useMemo(() => {
    if (!compareMetrics) return null
    const calc = (cur: number, prev: number) => prev > 0 ? ((cur - prev) / prev) * 100 : null
    return {
      total: calc(report?.totalNetSales ?? 0, compareMetrics.total),
      orders: calc(report?.totalOrders ?? 0, compareMetrics.orders),
      avg: calc(summaryMetrics.avg, compareMetrics.avg),
      upt: calc(summaryMetrics.upt, compareMetrics.upt),
    }
  }, [compareMetrics, report, summaryMetrics])

  // ---- Actions ----
  const runQuery = useCallback(
    async (range: DateRange) => {
      if (!range.from || !range.to) return
      try {
        await getSalesReport(range.from, range.to, forceShopify)
      } catch (e) {
        console.error('Error getting sales report', e)
      }
    },
    [getSalesReport, forceShopify]
  )

  const dateLabel = useMemo(() => {
    const { from, to } = dateRange
    if (!from || !to) return 'Selecciona rango'

    if (isSameDay(from, to)) {
      return format(from, "d 'de' MMMM, yyyy", { locale: es })
    }

    return `${format(from, 'dd/MM/yyyy')} - ${format(to, 'dd/MM/yyyy')}`
  }, [dateRange])

  const handleApplyRange = useCallback(() => {
    runQuery(dateRange)
  }, [dateRange, runQuery])

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      runQuery(dateRange)
    }
  }, [dateRange, runQuery])

  const handleQuickRange = useCallback(
    (days: number) => {
      const to = endOfDay(new Date())
      const from = startOfDay(subDays(to, days - 1))
      setDateRange({ from, to })
    },
    []
  )

  const handleDownload = useCallback(() => {
    if (!report) return

    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `reporte-ventas-${format(dateRange.from!, 'yyyyMMdd')}-${format(
      dateRange.to!,
      'yyyyMMdd'
    )}.json`
    link.click()
    URL.revokeObjectURL(url)
  }, [report, dateRange])

  const handleExportPDF = useCallback(async () => {
    if (!dateRange.from || !dateRange.to || !hasDailyData) return

    const doc = new jsPDF()

    // Obtener el presupuesto del mes seleccionado
    const monthBudget = currentMonthBudget

    // Columna 1: Título y Período (50% izquierda)
    doc.setFontSize(16)
    doc.text(`Hoja de Metas de Ventas — ${company.name}`, 14, 15)

    doc.setFontSize(10)
    doc.text(
      `Período: ${format(dateRange.from, 'dd/MM/yyyy', { locale: es })} - ${format(dateRange.to, 'dd/MM/yyyy', { locale: es })}`,
      14,
      22
    )

    // Agregar información del mes y año seleccionado
    doc.setFontSize(9)
    doc.text(
      `Mes de Configuración: ${monthNames[selectedMonth]} ${selectedYear}`,
      14,
      28
    )

    // Si hay comparación con año anterior, mostrarla
    if (monthBudget.previousYearTotal) {
      const yoyGrowth = ((monthBudget.total - monthBudget.previousYearTotal) / monthBudget.previousYearTotal * 100).toFixed(1)
      doc.text(
        `Año Anterior: ${formatCurrency(monthBudget.previousYearTotal)} | Incremento: ${yoyGrowth}%`,
        14,
        33
      )
    }

    // Columna 2: Logo (50% derecha) — usa el logo configurado en /config
    const logo = await loadFirstPdfLogo([
      company.pdfLogoData,
      company.logoData,
      company.pdfLogoUrl,
      company.logoUrl,
    ])
    if (logo) {
      // Página vertical (A4 = 210mm ancho), margen derecho 14mm
      addLogoToPdf(doc, logo, 196, 6)
    }

    // Filtrar vendedores para exportar: solo aquellos con meta > 0 y rol asignado
    const sellersForExport = goalSellers.filter((seller) => {
      return (seller.goalPercentage ?? 0) > 0 && seller.role !== 'ninguno'
    })

    const tableBody = dailyDays.map((day) => {
      const sellerCells = sellersForExport.map((seller) =>
        formatCurrency(day.sellerData[seller.name]?.goal ?? 0)
      )

      return [
        day.date,
        day.dayName,
        formatCurrency(day.estimatedSales),
        ...sellerCells,
      ]
    })

    const totalsRow = [
      'TOTALES',
      format(dateRange.from, 'MMMM', { locale: es }).toUpperCase(),
      formatCurrency(monthBudget.total),
      ...sellersForExport.map((seller) =>
        formatCurrency((monthBudget.total * (seller.goalPercentage ?? 0)) / 100)
      ),
    ]
    tableBody.push(totalsRow)

    // Headers
    const headers = [
      'Fecha',
      'Día',
      'Venta Estimada',
      ...sellersForExport.map((seller) => formatSellerName(seller.name)),
    ]

    // Generar tabla
    const columnStyles: Record<number, any> = {
      0: { halign: 'left' },
      1: { halign: 'left' },
    }

    headers.forEach((_, index) => {
      if (index >= 2) {
        columnStyles[index] = { halign: 'right' }
      }
    })

    autoTable(doc, {
      head: [headers],
      body: tableBody,
      startY: monthBudget.previousYearTotal ? 38 : 33,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles,
      didParseCell: (data: any) => {
        // Hacer bold la última fila (TOTALES)
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [231, 233, 237]
        }
      },
    })

    // Guardar PDF con nombre descriptivo incluyendo mes y año
    doc.save(
      `metas-ventas-${monthNames[selectedMonth]}-${selectedYear}-${format(dateRange.from, 'yyyyMMdd')}-${format(
        dateRange.to,
        'yyyyMMdd'
      )}.pdf`
    )
  }, [currentMonthBudget, dateRange, dailyDays, goalSellers, hasDailyData, selectedMonth, selectedYear, company])

  const handleExportActualsPDF = useCallback(async () => {
    if (!dateRange.from || !dateRange.to || !hasDailyData || dailyDays.length === 0) return

    const doc = new jsPDF({ orientation: 'landscape' })

    // Logo configurado en /config (página horizontal A4 = 297mm ancho)
    const logo = await loadFirstPdfLogo([
      company.pdfLogoData,
      company.logoData,
      company.pdfLogoUrl,
      company.logoUrl,
    ])
    if (logo) {
      addLogoToPdf(doc, logo, 283, 6)
    }

    doc.setFontSize(16)
    doc.text(`Ventas por Vendedor — ${company.name}`, 14, 15)
    doc.setFontSize(10)
    doc.text(
      `Período: ${format(dateRange.from, 'dd/MM/yyyy', { locale: es })} – ${format(dateRange.to, 'dd/MM/yyyy', { locale: es })}`,
      14, 22
    )
    doc.setFontSize(9)
    doc.text(`Mes: ${monthNames[selectedMonth]} ${selectedYear}`, 14, 28)

    // Collect sellers with actual sales, sorted by total desc
    const sellerTotals = new Map<string, number>()
    dailyDays.forEach(day => {
      rawSellersWithGoals.forEach(s => {
        const v = day.sellerData[s.name]?.actual || 0
        if (v > 0) sellerTotals.set(s.name, (sellerTotals.get(s.name) || 0) + v)
      })
      rawSellersWithoutGoals.forEach(name => {
        const v = day.otherSellersData[name] || 0
        if (v > 0) sellerTotals.set(name, (sellerTotals.get(name) || 0) + v)
      })
    })

    const activeSellers = Array.from(sellerTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name)

    const tableBody = dailyDays.map(day => {
      let dayTotal = 0
      const cells = activeSellers.map(name => {
        const inGoals = rawSellersWithGoals.find(s => s.name === name)
        const amount = inGoals
          ? (day.sellerData[name]?.actual || 0)
          : (day.otherSellersData[name] || 0)
        dayTotal += amount
        return amount > 0 ? formatCurrency(amount) : '-'
      })
      return [day.date, day.dayName, ...cells, formatCurrency(dayTotal)]
    })

    const totalsRow: string[] = ['TOTALES', '']
    activeSellers.forEach(name => totalsRow.push(formatCurrency(sellerTotals.get(name) || 0)))
    totalsRow.push(formatCurrency(dailyDays.reduce((s, d) => s + d.totalDaySales, 0)))
    tableBody.push(totalsRow)

    const headers = ['Fecha', 'Día', ...activeSellers.map(n => formatSellerName(n)), 'Total']
    const colStyles: Record<number, any> = { 0: { halign: 'left' }, 1: { halign: 'left' } }
    headers.forEach((_, i) => { if (i >= 2) colStyles[i] = { halign: 'right' } })

    autoTable(doc, {
      head: [headers],
      body: tableBody,
      startY: 33,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 0, 0], textColor: 255, fontStyle: 'bold' },
      columnStyles: colStyles,
      didParseCell: (data: any) => {
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [231, 233, 237]
        }
      },
    })

    doc.save(`ventas-${monthNames[selectedMonth]}-${selectedYear}.pdf`)
  }, [dateRange, dailyDays, hasDailyData, selectedMonth, selectedYear, rawSellersWithGoals, rawSellersWithoutGoals, company])

  const handleToggleCompare = useCallback(() => {
    if (!compareMode && dateRange.from && dateRange.to) {
      const from = startOfDay(new Date(dateRange.from.getFullYear() - 1, dateRange.from.getMonth(), dateRange.from.getDate()))
      const to = endOfDay(new Date(dateRange.to.getFullYear() - 1, dateRange.to.getMonth(), dateRange.to.getDate()))
      setCompareDateRange({ from, to })
      getCompareReport(from, to)
    }
    setCompareMode(prev => !prev)
  }, [compareMode, dateRange, getCompareReport])

  const handleRunCompare = useCallback(() => {
    if (compareDateRange.from && compareDateRange.to) {
      getCompareReport(compareDateRange.from, compareDateRange.to)
    }
  }, [compareDateRange, getCompareReport])

  const handleBudgetSave = useCallback(
    async (next: BudgetConfig) => {
      setBudget(next)
      setIsBudgetDialogOpen(false)
      setHasBudgetBeenEdited(true)

      // Persist monthlyGoals locally so they survive page refreshes
      saveMonthlyGoalsToStorage(next.monthlyGoals)

      // Save to Firestore via Cloud Function
      try {

        const response = await FunctionsService.saveBudgetConfig(next)

        if (response.error) {
          toast({
            title: 'Presupuesto actualizado localmente',
            description: 'No se pudo guardar en la base de datos: ' + response.error,
            variant: 'destructive'
          })
        } else {
          toast({
            title: 'Presupuesto guardado',
            description: 'Configuración guardada en la base de datos'
          })
        }
      } catch (error: any) {
        toast({
          title: 'Presupuesto actualizado localmente',
          description: 'Error al guardar en la base de datos',
          variant: 'destructive'
        })
      }

      runQuery(dateRange)
    },
    [toast, runQuery, dateRange]
  )

  const handleClearCache = useCallback(async () => {
    try {
      // Clear report from memory first
      clearReport()

      const response = await FunctionsService.clearCache(
        dateRange.from && dateRange.to
          ? { from: dateRange.from, to: dateRange.to }
          : undefined
      )

      if (response.error) {
        toast({
          title: 'Error al limpiar caché',
          description: response.error,
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Caché limpiado',
          description: response.data?.message || 'El caché fue eliminado exitosamente (memoria + base de datos)'
        })

        // Refresh data after clearing cache
        if (dateRange.from && dateRange.to) {
          runQuery(dateRange)
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error al limpiar caché',
        description: error.message || 'Error inesperado',
        variant: 'destructive'
      })
    }
  }, [clearReport, toast, dateRange, runQuery])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.015]">
        <img src={company.watermarkUrl || '/nibpr.svg'} alt="Marca de agua" className="h-96 w-auto select-none" />
      </div>

      <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:px-10">
        <Header />

        <Card className="mt-8 border-white/10 bg-white/5 text-white backdrop-blur">
          <CardContent className="p-4 space-y-3">
            {/* Row 1: Date presets + calendar + budget config */}
            <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
              <Button variant="outline" size="sm" className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20" onClick={() => handleQuickRange(1)}>Hoy</Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20"
                onClick={() => {
                  const now = new Date()
                  const from = startOfDay(new Date(selectedYear, selectedMonth, 1))
                  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth()
                  const to = isCurrentMonth ? endOfDay(now) : endOfDay(new Date(selectedYear, selectedMonth + 1, 0))
                  setDateRange({ from, to })
                }}
              >Días del mes</Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20"
                onClick={() => {
                  const from = startOfDay(new Date(selectedYear, selectedMonth, 1))
                  const to = endOfDay(new Date(selectedYear, selectedMonth + 1, 0))
                  setDateRange({ from, to })
                }}
              >Mes completo</Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20">
                    <CalendarIcon className="mr-2 h-4 w-4" />{dateLabel}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-gray-900 border-white/20" align="start">
                  <Calendar
                    mode="range"
                    numberOfMonths={2}
                    selected={dateRange}
                    defaultMonth={dateRange.from}
                    onSelect={(value) => { if (!value) return; setDateRange({ from: value.from ? startOfDay(value.from) : undefined, to: value.to ? endOfDay(value.to) : undefined }) }}
                    locale={es}
                    disabled={(date) => isBefore(date, new Date(2024, 0, 1))}
                  />
                </PopoverContent>
              </Popover>
              <Select value={selectedMonth.toString()} onValueChange={(v) => {
                  const month = Number(v)
                  setSelectedMonth(month)
                  const from = startOfDay(new Date(selectedYear, month, 1))
                  const to = endOfDay(new Date(selectedYear, month + 1, 0))
                  setDateRange({ from, to })
                }}>
                <SelectTrigger className="flex-shrink-0 w-32 bg-white/10 text-white border-white/20 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 text-white border-white/20">
                  {monthNames.map((name, idx) => (<SelectItem key={idx} value={idx.toString()} className="hover:bg-white/10">{name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={selectedYear}
                onChange={(e) => {
                  const year = Number(e.target.value)
                  setSelectedYear(year)
                  if (year >= 2024 && year <= 2030) {
                    const from = startOfDay(new Date(year, selectedMonth, 1))
                    const to = endOfDay(new Date(year, selectedMonth + 1, 0))
                    setDateRange({ from, to })
                  }
                }}
                className="flex-shrink-0 w-20 bg-white/10 text-white border-white/20 h-9 text-sm"
                min={2024}
                max={2030}
              />
              <span className="flex-shrink-0 rounded-md bg-blue-500/10 border border-blue-500/30 px-2 py-1 text-xs text-blue-200 whitespace-nowrap">Meta: {formatCurrency(currentMonthBudget.total)}</span>
            </div>
            {/* Row 2: Actions */}
            <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
              <Button size="sm" onClick={handleApplyRange} className="flex-shrink-0 inline-flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-500/90" disabled={loading}>
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Generando...</> : <><RefreshCw className="h-4 w-4" />Generar</>}
              </Button>
              <Button variant="outline" size="sm" className="flex-shrink-0 inline-flex items-center gap-2 bg-red-500/20 text-red-200 hover:bg-red-500/30" onClick={handleClearCache} disabled={loading}>
                <Trash2 className="h-4 w-4" />Limpiar
              </Button>
              <Button variant="outline" size="sm" className="flex-shrink-0 inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20" onClick={() => setIsBudgetDialogOpen(true)}>
                <Settings className="h-4 w-4" />Presupuesto
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-shrink-0 inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20">
                    <FileJson className="h-4 w-4" />Info
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-gray-900/95 border-white/20 text-white backdrop-blur-xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Información del usuario</DialogTitle>
                    <DialogDescription className="text-white/60">Credenciales y herramientas de desarrollo</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-1">
                      <p className="text-sm text-white/50">Correo electronico</p>
                      <p className="text-base font-medium text-white">{user?.email ?? '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-white/50">Ultima sincronizacion</p>
                      <p className="text-base font-medium text-white">{report?.metadata?.generatedAt ? formatDateTime(new Date(report.metadata.generatedAt)) : '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-white/50">Origen de datos</p>
                      <p className="text-base font-medium text-white">{report?.metadata?.cacheSource ? CACHE_SOURCE_LABELS[report.metadata.cacheSource] ?? report.metadata.cacheSource : '-'}</p>
                    </div>
                    <div className="flex flex-col gap-2 pt-2 border-t border-white/10">
                      <Button variant="outline" className="w-full gap-2 bg-white/10 text-white hover:bg-white/20" onClick={handleDownload} disabled={!report}>
                        <Download className="h-4 w-4" />Exportar JSON
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full gap-2 bg-blue-500/20 text-blue-200 hover:bg-blue-500/30" disabled={!report}>
                            <FileJson className="h-4 w-4" />Ver JSON DEBUG
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl h-[80vh] bg-gray-900/95 border-white/20 text-white backdrop-blur-xl overflow-hidden flex flex-col">
                          <DialogHeader>
                            <DialogTitle className="text-2xl">Datos RAW del Reporte</DialogTitle>
                            <DialogDescription className="text-white/60">JSON completo que llega desde Cloud Functions</DialogDescription>
                          </DialogHeader>
                          <div className="flex-1 overflow-auto rounded-lg bg-black/50 p-4">
                            <pre className="text-xs text-green-300 whitespace-pre-wrap">{JSON.stringify(report, null, 2)}</pre>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={() => { navigator.clipboard.writeText(JSON.stringify(report, null, 2)); alert('JSON copiado al portapapeles!') }}>Copiar JSON</Button>
                            <Button variant="outline" className="flex-1" onClick={() => { const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `reporte-debug-${new Date().toISOString()}.json`; a.click(); URL.revokeObjectURL(url) }}>Descargar JSON</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <div className="flex-shrink-0 flex items-center gap-2 rounded-md bg-white/5 px-3 py-1.5">
                <Switch id="force-shopify" checked={forceShopify} onCheckedChange={setForceShopify} className="data-[state=checked]:bg-blue-500" />
                <Label htmlFor="force-shopify" className="cursor-pointer text-xs text-white whitespace-nowrap">Shopify {forceShopify && '✓'}</Label>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleCompare}
                className={cn(
                  'flex-shrink-0 inline-flex items-center gap-2 text-xs',
                  compareMode
                    ? 'bg-indigo-500/30 text-indigo-200 hover:bg-indigo-500/40 border-indigo-500/40'
                    : 'bg-white/10 text-white hover:bg-white/20'
                )}
              >
                <ArrowLeftRight className="h-4 w-4" />
                {compareMode ? 'Sin comparar' : 'Comparar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI grid — 10 cards: 5 cols × 2 rows on desktop, 2 cols × 5 rows on mobile */}
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          {/* Row 1 — monthly overview */}
          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Presupuesto {monthNames[selectedMonth]}</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{formatCurrency(currentMonthBudget.total)}</p>
              {currentMonthBudget.previousYearTotal && (
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs text-white/40">vs {formatCurrency(currentMonthBudget.previousYearTotal)}</span>
                  <span className={cn('text-xs font-semibold', currentMonthBudget.total > currentMonthBudget.previousYearTotal ? 'text-emerald-400' : 'text-red-400')}>
                    {((currentMonthBudget.total - currentMonthBudget.previousYearTotal) / currentMonthBudget.previousYearTotal * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Precio Promedio</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : formatCurrency(summaryMetrics.avg)}</p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">No. Pedidos</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : summaryMetrics.orders.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">UPT</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : summaryMetrics.upt.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Unidades de Venta</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : summaryMetrics.totalUnits.toLocaleString()}</p>
            </CardContent>
          </Card>

          {/* Row 2 — period KPIs */}
          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Presupuestado (período)</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : formatCurrency(periodBudget)}</p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Venta Real (período)</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-emerald-400 tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : formatCurrency(periodActualSales)}</p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Cumplimiento (período)</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className={cn('text-xl font-bold tabular-nums', periodFulfillment >= 100 ? 'text-emerald-400' : 'text-red-400')}>
                {loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : `${periodFulfillment.toFixed(1)}%`}
              </p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Cumplimiento (mes)</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">
                {loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : `${currentMonthBudget.total > 0 ? (((report?.totalNetSales ?? 0) / currentMonthBudget.total) * 100).toFixed(1) : 0}%`}
              </p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60">Impuestos (período)</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-blue-400 tabular-nums">{loading ? <Skeleton className="h-7 w-24 bg-white/20" /> : formatCurrency(periodTaxes)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Section */}
        {compareMode && (
          <section className="mt-4">
            <Card className="border-indigo-500/20 bg-indigo-950/20 text-white backdrop-blur overflow-hidden">
              <CardHeader className="py-4 px-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                      <ArrowLeftRight className="h-4 w-4 text-indigo-400" />
                      Comparación de Períodos
                    </CardTitle>
                    <CardDescription className="text-xs text-white/50 mt-0.5">
                      <span className="text-emerald-400 font-medium">
                        {dateRange.from && dateRange.to ? `${format(dateRange.from, 'dd/MM/yyyy', { locale: es })} – ${format(dateRange.to, 'dd/MM/yyyy', { locale: es })}` : '—'}
                      </span>
                      {' '}vs{' '}
                      <span className="text-indigo-400 font-medium">
                        {compareDateRange.from && compareDateRange.to ? `${format(compareDateRange.from, 'dd/MM/yyyy', { locale: es })} – ${format(compareDateRange.to, 'dd/MM/yyyy', { locale: es })}` : '—'}
                      </span>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1.5 bg-white/5 text-white border-white/10 text-xs h-7 px-2">
                          <CalendarIcon className="h-3.5 w-3.5" />
                          Cambiar período
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 bg-gray-900 border-white/20" align="end">
                        <Calendar
                          mode="range"
                          numberOfMonths={2}
                          selected={compareDateRange}
                          defaultMonth={compareDateRange.from}
                          onSelect={(v) => { if (!v) return; setCompareDateRange({ from: v.from ? startOfDay(v.from) : undefined, to: v.to ? endOfDay(v.to) : undefined }) }}
                          locale={es}
                          disabled={(date) => isBefore(date, new Date(2024, 0, 1))}
                        />
                      </PopoverContent>
                    </Popover>
                    <Button
                      size="sm"
                      onClick={handleRunCompare}
                      disabled={compareLoading || !compareDateRange.from || !compareDateRange.to}
                      className="gap-1.5 bg-indigo-500 text-white hover:bg-indigo-500/90 text-xs h-7 px-3"
                    >
                      {compareLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      {compareLoading ? 'Cargando...' : 'Actualizar'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-5">
                {/* Metric summary cards */}
                {compareMetrics && (
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {([
                      { label: 'Ventas Totales', cur: report?.totalNetSales ?? 0, prev: compareMetrics.total, delta: compareDeltas?.total, fmt: formatCurrency },
                      { label: 'Pedidos', cur: report?.totalOrders ?? 0, prev: compareMetrics.orders, delta: compareDeltas?.orders, fmt: (v: number) => v.toLocaleString() },
                      { label: 'Precio Promedio', cur: summaryMetrics.avg, prev: compareMetrics.avg, delta: compareDeltas?.avg, fmt: formatCurrency },
                      { label: 'UPT', cur: summaryMetrics.upt, prev: compareMetrics.upt, delta: compareDeltas?.upt, fmt: (v: number) => v.toFixed(2) },
                    ] as const).map(({ label, cur, prev, delta, fmt }) => (
                      <div key={label} className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-1">
                        <p className="text-xs text-white/50">{label}</p>
                        <p className="text-lg font-bold text-emerald-400 tabular-nums">{fmt(cur as number)}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-indigo-300 tabular-nums">{fmt(prev as number)}</span>
                          {delta != null && (
                            <span className={cn('flex items-center gap-0.5 text-xs font-semibold', delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-white/40')}>
                              {delta > 0 ? <TrendingUp className="h-3 w-3" /> : delta < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                              {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bar chart */}
                {compareChartData.length > 0 && (
                  <div>
                    <p className="text-xs text-white/40 mb-3">Ventas diarias por período</p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={compareChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barGap={2}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                          <XAxis
                            dataKey="dia"
                            tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            label={{ value: 'Día del período', position: 'insideBottom', offset: -2, fill: 'rgba(255,255,255,0.2)', fontSize: 10 }}
                          />
                          <YAxis
                            tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`}
                            width={48}
                          />
                          <RechartsTooltip
                            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                            contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', fontSize: '12px' }}
                            labelStyle={{ color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}
                            labelFormatter={(v, payload) => {
                              if (!payload || payload.length === 0) return `Día ${v}`
                              const d = payload[0]?.payload
                              return `Día ${v} — ${d?.labelActual || ''} / ${d?.labelAnterior || ''}`
                            }}
                            formatter={(value: number, name: string) => [formatCurrency(value), name]}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                            formatter={(value) => <span style={{ color: 'rgba(255,255,255,0.55)' }}>{value}</span>}
                          />
                          <Bar
                            dataKey="actual"
                            name={`${dateRange.from ? dateRange.from.getFullYear() : selectedYear}`}
                            fill="#10b981"
                            radius={[3, 3, 0, 0]}
                            maxBarSize={28}
                          />
                          <Bar
                            dataKey="anterior"
                            name={`${compareDateRange.from ? compareDateRange.from.getFullYear() : selectedYear - 1}`}
                            fill="#6366f1"
                            radius={[3, 3, 0, 0]}
                            maxBarSize={28}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {!compareMetrics && !compareLoading && (
                  <div className="text-center text-white/30 text-sm py-8">
                    Haz clic en "Actualizar" para cargar los datos del período a comparar
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        )}

        {/* Daily table */}
        <section className="mt-4">
          <Card className="border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
            <CardHeader className="py-4 px-5">
              <div className="flex flex-row items-center justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-sm font-semibold text-white">Reporte de Ventas Diarias</CardTitle>
                  <CardDescription className="text-xs text-white/50 mt-0.5">Ventas reales vs. estimadas por día</CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 bg-white/10 text-white hover:bg-white/20 border-white/10 text-xs h-7 px-2"
                    onClick={handleExportPDF}
                    disabled={!dateRange.from || !dateRange.to || !hasDailyData}
                    title="PDF de metas vs ventas reales"
                  >
                    <Download className="h-3.5 w-3.5" />
                    PDF Metas
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border-emerald-500/20 text-xs h-7 px-2"
                    onClick={handleExportActualsPDF}
                    disabled={!dateRange.from || !dateRange.to || !hasDailyData}
                    title="PDF de ventas reales por vendedor"
                  >
                    <Download className="h-3.5 w-3.5" />
                    PDF Ventas
                  </Button>
                  <div className="flex items-center gap-2 rounded-md bg-white/5 px-3 py-1.5">
                    <Switch id="only-goals" checked={showOnlyGoalSellers} onCheckedChange={(checked) => setShowOnlyGoalSellers(Boolean(checked))} />
                    <Label htmlFor="only-goals" className="text-xs text-white/70 cursor-pointer">
                      Solo metas
                    </Label>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {loading ? (
                <div className="space-y-3">
                  <Skeleton className="h-10 w-full bg-white/10" />
                  <Skeleton className="h-10 w-full bg-white/10" />
                  <Skeleton className="h-10 w-full bg-white/10" />
                </div>
              ) : !hasDailyData || dailyDays.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/10 p-12 text-center text-white/60">
                  No hay datos disponibles para el rango seleccionado.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/70">Fecha</TableHead>
                      <TableHead className="text-white/70">Día</TableHead>
                      <TableHead className="text-white/70 text-right">Venta Estimada</TableHead>
                      {displaySellersWithGoals.map((seller) => (
                        <TableHead key={seller.name} className="text-white/70 text-right">
                          {formatSellerName(seller.name)}
                        </TableHead>
                      ))}
                      {!showOnlyGoalSellers &&
                        displaySellersWithoutGoals.map((sellerName) => (
                          <TableHead key={sellerName} className="text-white/70 text-right">
                            {formatSellerName(sellerName)}
                          </TableHead>
                        ))}
                      {!showOnlyGoalSellers && (
                        <>
                          <TableHead className="text-white/70 text-right">Venta Día Total</TableHead>
                          <TableHead className="text-white/70 text-right">% Cumplimiento</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyDays.map((day) => {
                      return (
                        <TableRow key={day.date} className="border-white/5">
                          <TableCell className="text-white">{day.date}</TableCell>
                          <TableCell className="text-white/70">{day.dayName}</TableCell>
                          <TableCell className="text-right text-white">{formatCurrency(day.estimatedSales)}</TableCell>
                          {displaySellersWithGoals.map((seller) => {
                            const data = day.sellerData[seller.name] || { actual: 0, goal: 0 }
                            const achieved = data.goal > 0 ? data.actual >= data.goal : false
                            const displayValue = showOnlyGoalSellers ? data.goal : data.actual
                            return (
                              <TableCell key={seller.name} className="text-right">
                                <div
                                  className={cn(
                                    'font-semibold',
                                    showOnlyGoalSellers ? 'text-white' : achieved ? 'text-emerald-400' : 'text-red-400'
                                  )}
                                >
                                  {formatCurrency(displayValue)}
                                </div>
                                {!showOnlyGoalSellers && (
                                  <div className="text-xs text-white/50">de {formatCurrency(data.goal)}</div>
                                )}
                              </TableCell>
                            )
                          })}
                          {!showOnlyGoalSellers &&
                            displaySellersWithoutGoals.map((sellerName) => {
                              const sales = day.otherSellersData[sellerName] || 0
                              return (
                                <TableCell key={sellerName} className="text-right">
                                  <div className="font-semibold text-white">{formatCurrency(sales)}</div>
                                </TableCell>
                              )
                            })}
                          {!showOnlyGoalSellers && (
                            <TableCell className="text-right font-semibold text-white">
                              {formatCurrency(day.totalDaySales)}
                            </TableCell>
                          )}
                          {!showOnlyGoalSellers && (
                            <TableCell
                              className={cn(
                                'text-right font-semibold',
                                day.fulfillment >= 100 ? 'text-emerald-400' : 'text-red-400'
                              )}
                            >
                              {day.fulfillment.toFixed(1)}%
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                    {hasDailyData && dailyDays.length > 0 && (
                      <TableRow className="border-white/10 bg-white/5">
                        <TableCell className="font-bold text-white">TOTALES</TableCell>
                        <TableCell className="text-white/70">
                          {format(dateRange.from!, 'MMMM', { locale: es }).toUpperCase()}
                        </TableCell>
                        <TableCell className="text-right font-bold text-white">
                          {formatCurrency(dailyDays.reduce((sum, d) => sum + d.estimatedSales, 0))}
                        </TableCell>
                        {displaySellersWithGoals.map((seller) => {
                          const total = dailyDays.reduce(
                            (sum, d) =>
                              sum +
                              (showOnlyGoalSellers
                                ? d.sellerData[seller.name]?.goal || 0
                                : d.sellerData[seller.name]?.actual || 0),
                            0
                          )
                          return (
                            <TableCell key={seller.name} className="text-right font-bold text-white">
                              {formatCurrency(total)}
                            </TableCell>
                          )
                        })}
                        {!showOnlyGoalSellers &&
                          displaySellersWithoutGoals.map((sellerName) => {
                            const total = dailyDays.reduce((sum, d) => sum + (d.otherSellersData[sellerName] || 0), 0)
                            return (
                              <TableCell key={sellerName} className="text-right font-bold text-white">
                                {formatCurrency(total)}
                              </TableCell>
                            )
                          })}
                        {!showOnlyGoalSellers && (
                          <TableCell className="text-right font-bold text-white">
                            {formatCurrency(dailyDays.reduce((sum, d) => sum + d.totalDaySales, 0))}
                          </TableCell>
                        )}
                        {!showOnlyGoalSellers && (
                          <TableCell className="text-right font-bold text-white">
                            {(() => {
                              const totalEst = dailyDays.reduce((sum, d) => sum + d.estimatedSales, 0)
                              const totalAct = dailyDays.reduce((sum, d) => sum + d.totalDaySales, 0)
                              return `${totalEst > 0 ? ((totalAct / totalEst) * 100).toFixed(1) : 0}%`
                            })()}
                          </TableCell>
                        )}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        {error && (
          <div className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>
        )}
      </div>

      <BudgetSettingsDialog open={isBudgetDialogOpen} onOpenChange={setIsBudgetDialogOpen} budget={budget} onSave={handleBudgetSave} sellers={budgetSellers} />
    </div>
  )
}

export default StatsDashboard

// -------------------- Budget Dialog --------------------

interface BudgetSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  budget: BudgetConfig
  onSave: (budget: BudgetConfig) => void
  sellers: { name: string }[]
}

function BudgetSettingsDialog({ open, onOpenChange, budget, onSave, sellers }: BudgetSettingsDialogProps) {
  const [currentBudget, setCurrentBudget] = useState<BudgetConfig>(() => ({
    ...budget,
    dailyWeights: { ...defaultDailyWeights, ...budget.dailyWeights },
    sellerGoals: sellers.map(({ name }) => {
      const existing = budget.sellerGoals.find((goal) => goal.name === name)
      return (
        existing ?? {
          name,
          role: 'ninguno',
          goalPercentage: 0,
        }
      )
    }),
  }))

  // Estado para el mes que se está editando
  const [editingMonth, setEditingMonth] = useState<number>(budget.currentMonth ?? new Date().getMonth())

  // Estado para distribución inteligente
  const [useIntelligentWeights, setUseIntelligentWeights] = useState(false)
  const [dayImportance, setDayImportance] = useState<Record<number, number>>({
    0: 3, // Domingo - Media
    1: 2, // Lunes - Baja
    2: 2, // Martes - Baja
    3: 2, // Miércoles - Baja
    4: 4, // Jueves - Alta
    5: 5, // Viernes - Muy Alta
    6: 5, // Sábado - Muy Alta
  })

  // Estado para año anterior
  const [previousYearTotal, setPreviousYearTotal] = useState<number | undefined>(undefined)
  const [growthPercentage, setGrowthPercentage] = useState<number | undefined>(undefined)

  // Inicializar valores cuando se abre el diálogo o cambia el mes
  useEffect(() => {
    if (open) {
      const monthConfig = budget.monthlyGoals?.[editingMonth] ?? {
        total: budget.total,
        previousYearTotal: undefined,
        sellerGoals: budget.sellerGoals,
        dailyWeights: budget.dailyWeights,
        dayImportance: undefined,
      }

      setCurrentBudget({
        ...budget,
        total: monthConfig.total,
        dailyWeights: { ...defaultDailyWeights, ...monthConfig.dailyWeights },
        sellerGoals: sellers.map(({ name }) => {
          const existing = monthConfig.sellerGoals.find((goal) => goal.name === name)
          if (!existing) {
            return {
              name,
              role: 'ninguno' as SellerRole,
              goalPercentage: 0,
              goalAmount: 0,
            }
          }

          // Calcular goalAmount si no existe (compatibilidad con datos antiguos)
          const goalAmount = existing.goalAmount ||
            (monthConfig.total * (existing.goalPercentage / 100))

          return {
            ...existing,
            goalAmount,
          }
        }),
      })

      setPreviousYearTotal(monthConfig.previousYearTotal)
      setGrowthPercentage(undefined)

      if (monthConfig.dayImportance) {
        setDayImportance(monthConfig.dayImportance)
        setUseIntelligentWeights(true)
      } else {
        setUseIntelligentWeights(false)
      }
    }
  }, [budget, open, editingMonth, sellers])

  const handleRoleChange = (name: string, role: SellerRole) => {
    setCurrentBudget((prev) => ({
      ...prev,
      sellerGoals: prev.sellerGoals.map((goal) => (goal.name === name ? { ...goal, role } : goal)),
    }))
  }

  const handleGoalAmountChange = (name: string, value: string) => {
    const numeric = Number(value)
    if (Number.isNaN(numeric) || numeric < 0) return

    setCurrentBudget((prev) => {
      // Calcular el porcentaje automáticamente
      const percentage = prev.total > 0 ? (numeric / prev.total) * 100 : 0

      return {
        ...prev,
        sellerGoals: prev.sellerGoals.map((goal) =>
          goal.name === name
            ? {
                ...goal,
                goalAmount: numeric,
                goalPercentage: percentage,
              }
            : goal
        ),
      }
    })
  }

  // Note: handleGoalPercentageChange replaced by handleGoalAmountChange
  // Percentages are now auto-calculated from monetary values

  const handleWeightChange = (dayIndex: number, role: keyof DailyWeight, value: string) => {
    const numeric = Number(value)
    if (Number.isNaN(numeric) || numeric < 0) return

    setCurrentBudget((prev) => ({
      ...prev,
      dailyWeights: {
        ...prev.dailyWeights,
        [dayIndex]: {
          ...prev.dailyWeights[dayIndex],
          [role]: numeric,
        },
      },
    }))
  }

  const totalAssignedPercentage = useMemo(() => {
    return currentBudget.sellerGoals.reduce((sum, goal) => sum + (Number(goal.goalPercentage) || 0), 0)
  }, [currentBudget.sellerGoals])

  const totalAssignedAmount = useMemo(() => {
    return currentBudget.sellerGoals.reduce((sum, goal) => sum + (Number(goal.goalAmount) || 0), 0)
  }, [currentBudget.sellerGoals])

  const handleSave = () => {
    // Calcular pesos inteligentes si está activado
    let finalWeights = currentBudget.dailyWeights
    if (useIntelligentWeights) {
      finalWeights = calculateIntelligentDailyWeights(
        dayImportance,
        editingMonth,
        new Date().getFullYear()
      )
    }

    // Crear la configuración mensual
    const monthlyGoal: MonthlyGoal = {
      total: currentBudget.total,
      previousYearTotal: previousYearTotal,
      sellerGoals: currentBudget.sellerGoals.map((goal) => ({
        ...goal,
        goalAmount: Number(goal.goalAmount) || 0,
        goalPercentage: Number(goal.goalPercentage) || 0,
      })),
      dailyWeights: finalWeights,
      dayImportance: useIntelligentWeights ? dayImportance : undefined,
    }

    // Actualizar el budget completo con la nueva configuración mensual
    const updatedBudget: BudgetConfig = {
      ...budget,
      // Mantener compatibilidad con estructura legacy
      total: monthlyGoal.total,
      sellerGoals: monthlyGoal.sellerGoals,
      dailyWeights: monthlyGoal.dailyWeights,
      // Agregar/actualizar configuración mensual
      monthlyGoals: {
        ...budget.monthlyGoals,
        [editingMonth]: monthlyGoal,
      },
      currentMonth: editingMonth,
    }

    onSave(updatedBudget)
  }

  const handleReset = () => {
    const newBudget = {
      total: defaultBudget.total,
      dailyWeights: { ...defaultDailyWeights },
      sellerGoals: defaultSellerGoals.map(goal => ({ ...goal })),
    }
    setCurrentBudget(newBudget)

    // Mostrar confirmación visual
    window.alert(
      '✅ Configuración reseteada a valores por defecto:\n\n' +
      `• Presupuesto: $${defaultBudget.total.toLocaleString()}\n` +
      `• Sebastian R.: 36.74%\n` +
      `• Secundarios (5): 12.65% cada uno\n\n` +
      'Haz clic en "Guardar cambios" para aplicar.'
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto border-white/10 bg-gray-950/90 text-white backdrop-blur-xl lg:max-w-6xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Configuracion de presupuesto mensual</DialogTitle>
          <DialogDescription className="text-white/70">
            Ajusta la meta de ventas, los roles de cada vendedor y la contribucion diaria por rol.
          </DialogDescription>
        </DialogHeader>

        {/* Selector de Mes */}
        <div className="flex items-center gap-4 rounded-lg bg-blue-500/10 border border-blue-500/30 p-4">
          <Label className="text-sm font-medium text-white">Configurar mes:</Label>
          <Select
            value={editingMonth.toString()}
            onValueChange={(v) => setEditingMonth(Number(v))}
          >
            <SelectTrigger className="w-48 bg-white/10 text-white border-white/20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 text-white border-white/20">
              {monthNames.map((name, idx) => (
                <SelectItem key={idx} value={idx.toString()} className="hover:bg-white/10">
                  {name} {new Date().getFullYear()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-white/10 bg-white/5">
              <CardHeader className="space-y-1">
                <CardTitle className="text-lg text-white">Presupuesto mensual total</CardTitle>
                <CardDescription className="text-white/60">Define la meta global de ventas para el equipo.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="prev-year-total" className="flex items-center gap-2">
                    Año anterior (USD)
                    <span className="text-xs text-white/40">(Base para cálculo)</span>
                  </Label>
                  <Input
                    id="prev-year-total"
                    type="number"
                    min={0}
                    value={previousYearTotal ?? ''}
                    onChange={(event) => {
                      const inputValue = event.target.value
                      if (inputValue === '') {
                        setPreviousYearTotal(undefined)
                        return
                      }
                      const numValue = Number(inputValue)
                      if (!isNaN(numValue) && numValue >= 0) {
                        setPreviousYearTotal(numValue)
                        // Auto-calcular presupuesto si hay % de crecimiento
                        if (growthPercentage !== undefined) {
                          const newTotal = numValue * (1 + growthPercentage / 100)
                          setCurrentBudget((prev) => ({ ...prev, total: Math.round(newTotal) }))
                        }
                      }
                    }}
                    placeholder="Ej: 42000"
                    className="bg-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="growth-percentage" className="flex items-center gap-2">
                    % Incremento
                    <span className="text-xs text-white/40">(Auto-calcula objetivo)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="growth-percentage"
                      type="number"
                      min={-100}
                      max={500}
                      step="0.1"
                      value={growthPercentage ?? ''}
                      onChange={(event) => {
                        const inputValue = event.target.value
                        if (inputValue === '') {
                          setGrowthPercentage(undefined)
                          return
                        }
                        const numValue = Number(inputValue)
                        if (!isNaN(numValue) && numValue >= -100 && numValue <= 500) {
                          setGrowthPercentage(numValue)
                          // Auto-calcular presupuesto
                          if (previousYearTotal !== undefined) {
                            const newTotal = previousYearTotal * (1 + numValue / 100)
                            setCurrentBudget((prev) => ({ ...prev, total: Math.round(newTotal) }))
                          }
                        }
                      }}
                      placeholder="Ej: 9.5"
                      className="bg-white/10 text-white pr-8"
                    />
                    <Percent className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                  </div>
                  {previousYearTotal !== undefined && growthPercentage !== undefined && (
                    <p className="text-xs text-emerald-400">
                      ✓ Objetivo auto-calculado: {formatCurrency(Math.round(previousYearTotal * (1 + growthPercentage / 100)))}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dialog-total-budget">
                    Monto objetivo (USD)
                    {previousYearTotal && growthPercentage !== undefined && (
                      <span className="ml-2 text-xs text-emerald-400">✓ Auto-calculado</span>
                    )}
                  </Label>
                  <Input
                    id="dialog-total-budget"
                    type="number"
                    min={0}
                    value={currentBudget.total}
                    onChange={(event) => setCurrentBudget((prev) => ({ ...prev, total: Number(event.target.value) || 0 }))}
                    className="bg-white/10 text-lg font-semibold text-white"
                    placeholder="Editable manualmente"
                  />
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-white/60">
                    Total asignado:{' '}
                    <span
                      className={cn(
                        totalAssignedAmount > currentBudget.total
                          ? 'text-red-400'
                          : totalAssignedAmount < currentBudget.total
                          ? 'text-amber-300'
                          : 'text-emerald-400',
                        'font-semibold text-lg'
                      )}
                    >
                      {formatCurrency(totalAssignedAmount)}
                    </span>
                  </p>
                  <p className="text-xs text-white/50">
                    Diferencia: {formatCurrency(currentBudget.total - totalAssignedAmount)}
                    {' '}({totalAssignedPercentage.toFixed(1)}% del total)
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/5">
              <CardHeader className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-white">Distribución Diaria</CardTitle>
                    <CardDescription className="text-white/60">
                      {useIntelligentWeights
                        ? 'Configura la importancia de cada día (automático)'
                        : 'Porcentaje manual del presupuesto mensual por día'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="intelligent-toggle" className="text-xs text-white/70 cursor-pointer">
                      Inteligente
                    </Label>
                    <Switch
                      id="intelligent-toggle"
                      checked={useIntelligentWeights}
                      onCheckedChange={setUseIntelligentWeights}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {useIntelligentWeights ? (
                  <div className="space-y-3">
                    <p className="text-xs text-white/50 bg-blue-500/10 border border-blue-500/20 rounded p-2">
                      💡 Selecciona la importancia de ventas para cada día. El sistema calculará automáticamente los pesos basándose en:
                      frecuencia del día en el mes, nivel de importancia, y multiplicadores por rol.
                    </p>
                    {daysOfWeek.map(({ index, label }) => (
                      <div key={label} className="flex items-center gap-3">
                        <Label className="w-24 text-sm text-white/80">{label}</Label>
                        <Select
                          value={dayImportance[index]?.toString() || '3'}
                          onValueChange={(v) => setDayImportance(prev => ({ ...prev, [index]: Number(v) }))}
                        >
                          <SelectTrigger className="flex-1 bg-white/10 text-white border-white/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 text-white border-white/20">
                            <SelectItem value="1" className="hover:bg-white/10">⭐ Baja</SelectItem>
                            <SelectItem value="2" className="hover:bg-white/10">⭐⭐ Media-Baja</SelectItem>
                            <SelectItem value="3" className="hover:bg-white/10">⭐⭐⭐ Media</SelectItem>
                            <SelectItem value="4" className="hover:bg-white/10">⭐⭐⭐⭐ Alta</SelectItem>
                            <SelectItem value="5" className="hover:bg-white/10">⭐⭐⭐⭐⭐ Muy Alta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {daysOfWeek.map(({ index, label }) => (
                      <div key={label} className="grid grid-cols-[120px_1fr_1fr] items-center gap-3">
                        <Label className="text-sm text-white/80">{label}</Label>
                        <div className="space-y-1">
                          <Label className="text-xs text-white/50">Principal</Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={currentBudget.dailyWeights[index]?.principal ?? 0}
                            onChange={(event) => handleWeightChange(index, 'principal', event.target.value)}
                            className="h-9 bg-white/10 text-right text-white"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-white/50">Secundario</Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={currentBudget.dailyWeights[index]?.secundario ?? 0}
                            onChange={(event) => handleWeightChange(index, 'secundario', event.target.value)}
                            className="h-9 bg-white/10 text-right text-white"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-sm text-white/70">Metas por vendedor (valores en USD)</Label>
              <span className="text-xs text-white/50">
                💡 Ingresa valores directos: más fácil que porcentajes
              </span>
            </div>
            <Card className="border-white/10 bg-white/5">
              <CardContent className="space-y-2 p-4">
                {currentBudget.sellerGoals.map((goal) => {
                  const calculatedPercentage = goal.goalPercentage || 0

                  return (
                    <div key={goal.name} className="grid grid-cols-12 items-center gap-3 rounded-md px-2 py-2 transition hover:bg-white/10">
                      <div className="col-span-3 text-sm text-white/80">{goal.name}</div>
                      <div className="col-span-3">
                        <Select value={goal.role} onValueChange={(value) => handleRoleChange(goal.name, value as SellerRole)}>
                          <SelectTrigger className="h-9 bg-white/10 text-white text-xs">
                            <SelectValue placeholder="Rol" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 text-white">
                            <SelectItem value="principal">Principal</SelectItem>
                            <SelectItem value="secundario">Secundario</SelectItem>
                            <SelectItem value="ninguno">Sin asignar</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-white/40">$</span>
                          <Input
                            type="number"
                            min={0}
                            step="100"
                            value={goal.goalAmount || ''}
                            onChange={(event) => handleGoalAmountChange(goal.name, event.target.value)}
                            placeholder="0"
                            className="h-9 bg-white/10 pl-6 text-right text-white"
                          />
                        </div>
                      </div>
                      <div className="col-span-2 text-right">
                        <span className={cn(
                          "text-xs font-medium",
                          calculatedPercentage > 0 ? "text-white/70" : "text-white/30"
                        )}>
                          {calculatedPercentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
          <Button onClick={handleReset} variant="outline" className="flex items-center gap-2 border-white/20 text-white hover:bg-white/10">
            <RefreshCw className="h-4 w-4" />
            Resetear a valores por defecto
          </Button>
          <Button onClick={handleSave} className="flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-500/90">
            <Save className="h-4 w-4" />
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
